import { FC } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import MuiPopper, { PopperProps as MuiPopperProps } from '@mui/material/Popper';
import { ClickAwayListener, unstable_composeClasses as composeClasses } from '@mui/material';
import Paper, { PaperProps as MuiPaperProps } from '@mui/material/Paper';
import { margin, MarginProps } from '@mui/system';
import { getToolbarUtilityClass } from './popperClasses';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system/createStyled';

interface PaperProps extends MuiPaperProps, MarginProps {
  maxWidth?: number | string;
}

export interface PopperProps extends MuiPopperProps {
  className?: string;
  zIndex?: number;
  PaperProps?: PaperProps;
  onClickAway: (event: MouseEvent | TouchEvent) => void;
}

type PopperOwnerState =
  | {
      classes: any;
      zIndex?: number;
      maxWidth?: number | string;
      PaperProps: PaperProps;
    }
  | any;

interface PopperPropsWithOwnerState extends MuiPopperProps {
  ownerState: PopperOwnerState;
}

interface PaperPropsWithOwnerState extends PaperProps {
  ownerState: PopperOwnerState;
}

const useUtilityClasses = (ownerState: PopperOwnerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ['root'],
    listener: ['wrapper'],
    paper: ['paper'],
  };

  const composedClasses = composeClasses(slots, getToolbarUtilityClass, classes);

  return {
    ...classes,
    ...composedClasses,
  };
};

const StyledPopper = styled(MuiPopper, {
  name: 'O2Popper',
  slot: 'Root',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'ownerState',
})<PopperPropsWithOwnerState>(({ theme, ownerState }) => {
  return {
    zIndex: ownerState.zIndex ?? theme.zIndex.modal,
  };
});

const StyledPaper = styled(Paper, {
  name: 'O2Popper',
  slot: 'Paper',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'ownerState',
})<PaperPropsWithOwnerState>(({ ownerState }) => ({
  borderRadius: '8px',
  boxShadow: '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)',
  maxWidth: ownerState.PaperProps.maxWidth,
  ...margin(ownerState.PaperProps),
  overflow: 'hidden',
}));

const Popper: FC<PopperProps> = (props) => {
  const { children, PaperProps, onClickAway, className, zIndex, ...other } = props;

  const ownerState: PopperOwnerState = {
    ...props,
    zIndex,
    PaperProps: {
      maxWidth: '450px',
      marginTop: 0,
      marginRight: 2,
      marginLeft: 2,
      ...PaperProps,
    },
  };

  const classes = useUtilityClasses(ownerState);

  return (
    <StyledPopper {...{ ownerState }} className={clsx(classes.root, className)} {...other}>
      <ClickAwayListener onClickAway={onClickAway}>
        <StyledPaper className={classes.paper} ownerState={ownerState} {...PaperProps}>
          {children}
        </StyledPaper>
      </ClickAwayListener>
    </StyledPopper>
  );
};

export default Popper;
